import { api } from './client'

// ---------- tipuri — oglindesc DTO-urile din Cipral.Application/Stocuri/{StockModels,InventoryModels}.cs ----------

export type StockMovementType = 'Receptie' | 'ConsumProductie' | 'RecepieProduseFinite' | 'TransferIntreZone' | 'Livrare' | 'AjustareInventar' | 'IntrareScrap'
export type StockStatus = 'Liber' | 'Blocat' | 'InControl' | 'Scrap'

export interface StockBalance {
  articleId: string; articleCode: string; articleDescription: string; warehouseId: string; warehouseName: string
  zoneId: string | null; zoneName: string | null; locationId: string | null; locationCode: string | null
  lotNumber: string | null; expiryDate: string | null; status: StockStatus; quantity: number
}

export interface StockAvailability {
  articleId: string; articleCode: string; articleDescription: string; warehouseId: string; warehouseName: string
  liberQuantity: number; reservedQuantity: number; availableQuantity: number; minStockQuantity: number | null; isBelowMinStock: boolean
  unitOfMeasureId: string
}

export interface StockAdjustmentPayload {
  articleId: string; warehouseId: string; zoneId: string | null; locationId: string | null; lotNumber: string | null
  status: StockStatus; quantity: number; reason: string
}

export interface StockMovement {
  id: string; createdAt: string; articleId: string; articleCode: string; articleDescription: string
  warehouseId: string; warehouseName: string; zoneId: string | null; zoneName: string | null
  locationId: string | null; locationCode: string | null; lotNumber: string | null; expiryDate: string | null
  type: StockMovementType; status: StockStatus; quantity: number; reference: string | null; createdByUserName: string
}

export interface TransferStockPayload {
  articleId: string; warehouseId: string; sourceZoneId: string | null; sourceLocationId: string | null
  destinationZoneId: string | null; destinationLocationId: string | null; lotNumber: string | null
  quantity: number; reference: string | null
}

export interface Lot {
  lotNumber: string; articleId: string; articleCode: string; articleDescription: string
  expiryDate: string | null; isExpired: boolean; totalQuantity: number; liberQuantity: number
}

export const listStockBalances = (params?: { warehouseId?: string; articleId?: string }) =>
  api.get<StockBalance[]>('/stocuri/stoc', { params }).then((r) => r.data)
export const listStockAvailability = () => api.get<StockAvailability[]>('/stocuri/stoc/disponibil').then((r) => r.data)
export const listMinStockAlerts = () => api.get<StockAvailability[]>('/stocuri/stoc/alerte-minim').then((r) => r.data)
export const adjustStock = (payload: StockAdjustmentPayload) => api.post('/stocuri/stoc/ajustare', payload)

export interface StockValuation {
  articleId: string; articleCode: string; articleDescription: string; warehouseId: string; warehouseName: string
  quantity: number; standardCost: number | null; value: number
}

export const getStockValuation = (params?: { warehouseId?: string; articleId?: string }) =>
  api.get<StockValuation[]>('/stocuri/stoc/valorizat', { params }).then((r) => r.data)
export const getStockValuationExportUrl = (params?: { warehouseId?: string; articleId?: string }) =>
  api.get<Blob>('/stocuri/stoc/valorizat/export', { params, responseType: 'blob' }).then((r) => r.data)

export const listStockMovements = (params?: { articleId?: string; warehouseId?: string; lotNumber?: string; from?: string; to?: string }) =>
  api.get<StockMovement[]>('/stocuri/miscari', { params }).then((r) => r.data)

export const listLots = () => api.get<Lot[]>('/stocuri/loturi').then((r) => r.data)
export const getLotHistory = (articleId: string, lotNumber: string) => api.get<StockMovement[]>(`/stocuri/loturi/${articleId}/${lotNumber}/istoric`).then((r) => r.data)
export const blockLot = (payload: { articleId: string; warehouseId: string; lotNumber: string; reason: string }) =>
  api.post('/stocuri/loturi/blocheaza', payload)

export const transferStock = (payload: TransferStockPayload) => api.post('/stocuri/transferuri', payload)

// ---------- FR-ADM-003b — transfer de stoc între puncte de lucru, cu stoc în tranzit ----------

export type StockTransferStatus = 'InTranzit' | 'Receptionat' | 'Anulat'

export interface StockTransferListItem {
  id: string; number: string; transferDate: string
  sourceWarehouseId: string; sourceWarehouseName: string
  destinationWarehouseId: string; destinationWarehouseName: string
  status: StockTransferStatus
}

export interface StockTransferLine { id: string; articleId: string; articleCode: string; articleDescription: string; quantity: number; lotNumber: string | null }

export interface StockTransferDetail extends StockTransferListItem {
  receivedAt: string | null; receivedByUserName: string | null; notes: string | null
  lines: StockTransferLine[]
}

export interface CreateStockTransferPayload {
  transferDate: string; sourceWarehouseId: string; destinationWarehouseId: string; notes: string | null
  lines: { articleId: string; quantity: number; lotNumber: string | null }[]
}

export const listStockTransfers = () => api.get<StockTransferListItem[]>('/stocuri/transferuri-stoc').then((r) => r.data)
export const getStockTransfer = (id: string) => api.get<StockTransferDetail>(`/stocuri/transferuri-stoc/${id}`).then((r) => r.data)
export const createStockTransfer = (payload: CreateStockTransferPayload) =>
  api.post<{ id: string }>('/stocuri/transferuri-stoc', payload).then((r) => r.data)
export const confirmStockTransfer = (id: string) => api.post(`/stocuri/transferuri-stoc/${id}/confirma`)
export const cancelStockTransfer = (id: string) => api.post(`/stocuri/transferuri-stoc/${id}/anuleaza`)

// ---------- inventar ----------

export type InventoryCountStatus = 'Draft' | 'Finalizat'
export type InventoryDifferenceReason = 'Scrap' | 'EroareNumarare' | 'Altul'

export interface InventoryCountLine {
  id: string; articleId: string; articleCode: string; articleDescription: string
  zoneId: string | null; zoneName: string | null; locationId: string | null; locationCode: string | null
  lotNumber: string | null; expectedQuantity: number; countedQuantity: number | null; differenceReason: InventoryDifferenceReason | null
}

export interface InventoryCountListItem { id: string; number: string; countDate: string; warehouseId: string; warehouseName: string; status: InventoryCountStatus }
export interface InventoryCountDetail extends InventoryCountListItem { notes: string | null; lines: InventoryCountLine[] }

export interface CreateInventoryCountPayload {
  countDate: string; warehouseId: string; notes: string | null
  lines: { articleId: string; zoneId: string | null; locationId: string | null; lotNumber: string | null }[]
}

export const listInventoryCounts = () => api.get<InventoryCountListItem[]>('/stocuri/inventar').then((r) => r.data)
export const getInventoryCount = (id: string) => api.get<InventoryCountDetail>(`/stocuri/inventar/${id}`).then((r) => r.data)
export const createInventoryCount = (payload: CreateInventoryCountPayload) =>
  api.post<{ id: string }>('/stocuri/inventar', payload).then((r) => r.data)
export const recordCount = (countId: string, lineId: string, payload: { countedQuantity: number; differenceReason: InventoryDifferenceReason | null }) =>
  api.post(`/stocuri/inventar/${countId}/linii/${lineId}/numara`, payload)
export const finalizeInventoryCount = (id: string) => api.post(`/stocuri/inventar/${id}/finalizeaza`)
export const getInventoryCountsExportUrl = (filters?: { from?: string; to?: string }) =>
  api.get<Blob>('/stocuri/inventar/export', { params: filters, responseType: 'blob' }).then((r) => r.data)
